/**
 * Updating Nova: asks the desktop shell (src-tauri/src/updates.rs), which checks
 * github.com/blacdev/Nova-Release and only installs a signed update.
 *
 * In a browser tab there is nothing to update -- the page is whatever the engine
 * serves -- so every call here is a no-op there.
 */

import { isDesktop } from "../platform";
import { readPref } from "./prefs";
import { dismissToast, noticeWithId, toast } from "../shell/Toast";

export interface UpdateInfo {
  version: string;
  currentVersion: string;
  notes?: string | null;
  date?: string | null;
}

/** The pref behind Settings → About → "Check when Nova starts". */
export const AUTO_CHECK_PREF = "nova.updates.auto";
/** The pref behind Settings → About → "Updates from". */
export const CHANNEL_PREF = "nova.updates.channel";

/**
 * Where updates come from: "stable" (versioned releases) or "staging" (the test
 * build CI publishes on every push to staging). A test build is numbered like
 * 0.2.0-22, so a build whose version has a "-" starts on the test channel.
 */
export const defaultChannel = (): string => (__NOVA_VERSION__.includes("-") ? "staging" : "stable");
export const updateChannel = (): string => readPref(CHANNEL_PREF, defaultChannel());

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: tauriInvoke } = await import("@tauri-apps/api/core");
  return tauriInvoke<T>(command, args);
}

/** A newer Nova, or null when this is the newest. Throws when the check fails. */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  if (!isDesktop()) return null;
  return invoke<UpdateInfo | null>("update_check", { channel: updateChannel() });
}

/** The check's error, in words: "nothing published yet" is not a failure. */
function why(err: unknown): { text: string; nothing: boolean } {
  const text = err instanceof Error ? err.message : String(err);
  if (text.startsWith("NOTHING_PUBLISHED")) {
    return {
      nothing: true,
      text: updateChannel() === "stable"
        ? "No version has been released yet, so there is nothing newer. Test builds are on the Staging channel (Settings → About)."
        : "No test build is published on this channel yet.",
    };
  }
  return { nothing: false, text: `Could not check for updates: ${text}` };
}

// ---------------------------------------------------------------- download and install

/**
 * When a downloaded update installs (Settings → About): "manual" waits for the
 * person to press Restart and install; "auto" installs and restarts as soon as the
 * download is verified; "exit" installs the next time Nova closes.
 */
export type InstallWhen = "manual" | "auto" | "exit";
export const INSTALL_PREF = "nova.updates.install";
export const installWhen = (): InstallWhen => readPref<InstallWhen>(INSTALL_PREF, "manual");

export interface UpdateStatus {
  phase: "idle" | "found" | "downloading" | "ready" | "failed";
  version: string | null;
  downloaded: number;
  total: number | null;
  error: string | null;
  installOnExit: boolean;
}

let status: UpdateStatus = { phase: "idle", version: null, downloaded: 0, total: null, error: null, installOnExit: false };
const listeners = new Set<() => void>();
const set = (next: UpdateStatus) => {
  status = next;
  listeners.forEach((fn) => fn());
};
export const updateStatus = () => status;
export function onUpdateStatus(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function readProgress() {
  try {
    set(await invoke<UpdateStatus>("update_progress"));
  } catch {
    // The shell answers this from memory; a failure here means no shell.
  }
}

/**
 * Download and verify the update the last check found, showing how far it has
 * got (Settings → About, and the status bar), then install it the way
 * Settings → About says.
 */
export async function downloadUpdate(): Promise<void> {
  if (!isDesktop() || status.phase === "downloading") return;
  // The "available" notice has done its job; the progress shows in About and the status bar.
  if (offered !== null) dismissToast(offered);
  offered = null;
  set({ ...status, phase: "downloading", downloaded: 0, total: null, error: null });
  const poll = window.setInterval(() => void readProgress(), 400);
  try {
    await invoke<void>("update_download");
  } catch (err) {
    window.clearInterval(poll);
    await readProgress();
    toast(`The update did not download: ${err instanceof Error ? err.message : String(err)}`, "bad", 8000);
    return;
  }
  window.clearInterval(poll);
  await readProgress();
  await applyInstallWhen();
}

/** After a download, or when the choice changes: act on "install when". */
export async function applyInstallWhen(): Promise<void> {
  if (status.phase !== "ready") return;
  const when = installWhen();
  await invoke<void>("update_install_on_exit", { on: when === "exit" }).catch(() => undefined);
  await readProgress();
  if (when === "auto") await installUpdate();
  else if (when === "exit") toast(`Nova ${status.version} is downloaded and installs when you close Nova.`, "ok", 8000);
  else toast(`Nova ${status.version} is downloaded.`, "ok", 15000, { label: "Restart and install", run: installUpdate });
}

/** Install the downloaded update and restart. Only returns if it failed. */
export async function installUpdate(): Promise<void> {
  try {
    await invoke<void>("update_install");
  } catch (err) {
    toast(`The update was not installed: ${err instanceof Error ? err.message : String(err)}`, "bad", 8000);
  }
}

/** Check, and say what was found. For the menu item and the About page. */
export async function checkAndTell(): Promise<UpdateInfo | null> {
  if (!isDesktop()) {
    toast("Updates apply to the desktop app; this page is served by the engine.", "info");
    return null;
  }
  try {
    const found = await checkForUpdate();
    await readProgress();
    if (found) offer(found);
    else toast(`Nova ${__NOVA_VERSION__} is the newest version.`, "ok");
    return found;
  } catch (err) {
    const w = why(err);
    toast(w.text, w.nothing ? "info" : "bad", 8000);
    return null;
  }
}

/** The "available" notice on screen, if any. */
let offered: number | null = null;

function offer(found: UpdateInfo) {
  void readProgress();
  if (status.phase === "downloading" || status.phase === "ready") return;
  if (offered !== null) dismissToast(offered);
  offered = noticeWithId(`Nova ${found.version} is available (you have ${found.currentVersion}).`, "info", 30000, {
    label: "Download",
    run: downloadUpdate,
  });
}

/**
 * Once per launch, a little after start, when the person has not turned it off.
 * Quiet unless there is something: no network is not worth a notice.
 */
export function checkOnStart(delayMs = 15000) {
  if (!isDesktop() || !readPref(AUTO_CHECK_PREF, true)) return;
  window.setTimeout(() => {
    void checkForUpdate()
      .then((found) => found && offer(found))
      .catch(() => {});
  }, delayMs);
}
